<template>
  <div class="vsm-field-selector">
    <div class="row" style="margin: 10px 0;">
      <div class="col-md-5" style="border: 1px solid #ccc; border-radius: 5px; padding: 10px; margin: 5px; min-height: 300px;">
        <h6 style="font-weight: bold; margin-bottom: 10px;">Available Fields</h6>
        <draggable
          v-model="availableFieldsList"
          :options="dragOptions"
          class="drag-list"
          @end="onDragEnd"
        >
          <div
            v-for="field in availableFieldsList"
            :key="field.key"
            class="field-item clickable"
            :title="field.label || field.key"
            @click="addField(field)"
          >
            <span v-if="field.label">{{ field.label }}</span>
            <span v-else>{{ field.key }}</span>
            <span style="float: right; color: green; font-weight: bold;">+</span>
          </div>
        </draggable>
      </div>
      <div class="col-md-1" style="display: flex; align-items: center; justify-content: center;">
        <div style="text-align: center;">
          <b-button size="sm" variant="success" @click="addAll" title="Add All">>></b-button>
          <br/><br/>
          <b-button size="sm" variant="danger" @click="removeAll" title="Remove All"><<</b-button>
        </div>
      </div>
      <div class="col-md-5" style="border: 1px solid #ccc; border-radius: 5px; padding: 10px; margin: 5px; min-height: 300px;">
        <h6 style="font-weight: bold; margin-bottom: 10px;">Selected Fields (drag to reorder)</h6>
        <draggable
          v-model="selectedFieldsList"
          :options="dragOptions"
          class="drag-list"
          @end="onDragEnd"
        >
          <div
            v-for="field in selectedFieldsList"
            :key="field.key"
            class="field-item selected"
            :title="field.label || field.key"
          >
            <span v-if="field.label">{{ field.label }}</span>
            <span v-else>{{ field.key }}</span>
            <b-button
              size="sm"
              variant="link"
              style="float: right; padding: 0; color: red;"
              @click="removeField(field)"
              title="Remove"
            >
              ×
            </b-button>
          </div>
        </draggable>
      </div>
    </div>
    <div style="margin-top: 10px;">
      <small class="text-muted">Click or drag fields from Available to Selected, or click the × button to remove. Drag within Selected Fields to reorder.</small>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import { VSM_FIELDS, VSM_HISTORY_FIELDS, getAvailableFields } from '../utils/vsm_fields'

export default {
  name: 'VsmFieldSelector',
  components: {
    draggable
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    fieldType: {
      type: String,
      default: 'main', // 'main' or 'history'
      validator: value => ['main', 'history'].includes(value)
    },
    availableFields: {
      type: Array,
      default: null // If provided, use this instead of all fields
    }
  },
  data() {
    return {
      selectedFieldsList: [],
      availableFieldsList: [],
      dragOptions: {
        group: 'fields',
        animation: 200,
        ghostClass: 'ghost'
      }
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.updateFields(newVal || '')
      }
    },
    selectedFieldsList: {
      deep: true,
      handler() {
        this.emitChange()
      }
    }
  },
  methods: {
    updateFields(fieldString) {
      const allFields = this.availableFields || (this.fieldType === 'history' ? VSM_HISTORY_FIELDS : VSM_FIELDS)
      
      // Parse the comma-delimited string
      const selectedKeys = fieldString ? fieldString.split(',').map(k => k.trim()).filter(k => k) : []
      
      // Build selected fields list
      this.selectedFieldsList = selectedKeys.map(key => {
        const field = allFields.find(f => f.key === key)
        return field || { key, label: key, sortable: false, visible: true }
      })
      
      // Build available fields list (all fields not in selected)
      const selectedKeysSet = new Set(selectedKeys)
      this.availableFieldsList = allFields.filter(f => !selectedKeysSet.has(f.key))
    },
    emitChange() {
      const fieldString = this.selectedFieldsList.map(f => f.key).join(',')
      this.$emit('input', fieldString)
    },
    addField(field) {
      // Add field to selected list if not already there
      const exists = this.selectedFieldsList.find(f => f.key === field.key)
      if (!exists) {
        this.selectedFieldsList.push(field)
        // Remove from available list
        const index = this.availableFieldsList.findIndex(f => f.key === field.key)
        if (index !== -1) {
          this.availableFieldsList.splice(index, 1)
        }
      }
    },
    removeField(field) {
      const index = this.selectedFieldsList.findIndex(f => f.key === field.key)
      if (index !== -1) {
        this.selectedFieldsList.splice(index, 1)
        this.availableFieldsList.push(field)
      }
    },
    addAll() {
      this.selectedFieldsList.push(...this.availableFieldsList)
      this.availableFieldsList = []
    },
    removeAll() {
      this.availableFieldsList.push(...this.selectedFieldsList)
      this.selectedFieldsList = []
    },
    onDragEnd() {
      // Ensure fields are properly moved between lists
      const allFields = this.availableFields || (this.fieldType === 'history' ? VSM_HISTORY_FIELDS : VSM_FIELDS)
      const allKeys = new Set(allFields.map(f => f.key))
      
      // Remove invalid fields from selected
      this.selectedFieldsList = this.selectedFieldsList.filter(f => allKeys.has(f.key))
      
      // Ensure available fields don't have duplicates
      const selectedKeys = new Set(this.selectedFieldsList.map(f => f.key))
      this.availableFieldsList = allFields.filter(f => !selectedKeys.has(f.key))
    }
  }
}
</script>

<style scoped>
.vsm-field-selector {
  width: 100%;
}

.drag-list {
  min-height: 250px;
}

.field-item {
  padding: 8px;
  margin: 5px 0;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 3px;
  cursor: move;
  user-select: none;
}

.field-item.clickable {
  cursor: pointer;
}

.field-item.clickable:hover {
  background-color: #e9ecef;
  border-color: #28a745;
}

.field-item.selected {
  background-color: #d4edda;
  border-color: #c3e6cb;
}

.field-item:hover {
  background-color: #e9ecef;
}

.field-item.selected:hover {
  background-color: #c3e6cb;
}

.ghost {
  opacity: 0.5;
  background-color: #cbd5e0;
}
</style>

